import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft, Trophy, FileDown } from "lucide-react";
import { BrimiLogo } from "@/components/OwlMascot";
import html2pdf from "html2pdf.js";
import { toast } from "sonner";

const Section = ({ title, children, testid }) => (
  <section className="mb-6" data-testid={testid}>
    <h3 className="font-outfit font-bold text-xl text-slate-900 border-b border-slate-200 pb-2 mb-3">{title}</h3>
    <div className="text-slate-700 leading-relaxed">{children}</div>
  </section>
);

const LabReport = () => {
  const { reportId } = useParams();
  const [report, setReport] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const reportRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/reports/${reportId}`);
        setReport(data);
      } catch (err) {
        console.error("Failed to load report:", err);
      }
    })();
  }, [reportId]);

  const downloadPdf = async () => {
    if (!reportRef.current) return;
    setDownloading(true);
    try {
      await html2pdf()
        .set({
          margin: [10, 10, 10, 10],
          filename: `BrimiAI_LabReport_${report.experiment_id}_${report.report_id}.pdf`,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          pagebreak: { mode: ["css", "legacy"] },
        })
        .from(reportRef.current)
        .save();
      toast.success("PDF downloaded 🎉");
    } catch (err) {
      toast.error("PDF generation failed");
    } finally {
      setDownloading(false);
    }
  };

  if (!report) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500">Loading report…</div>;
  }

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white">
      <div className="print:hidden">
        <Navbar />
      </div>

      <div className="max-w-4xl mx-auto p-6 print:p-0">
        <div className="flex items-center justify-between print:hidden mb-4">
          <Link to="/dashboard" className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" /> Dashboard
          </Link>
          <div className="flex gap-2">
            <Button
              onClick={downloadPdf}
              disabled={downloading}
              className="rounded-full bg-blue-600 hover:bg-blue-700"
              data-testid="report-download-pdf-btn"
            >
              <FileDown className="mr-2 h-4 w-4" />
              {downloading ? "Preparing…" : "Download PDF"}
            </Button>
            <Button onClick={() => window.print()} variant="outline" className="rounded-full" data-testid="report-print-btn">
              <Printer className="mr-2 h-4 w-4" /> Print
            </Button>
          </div>
        </div>

        <div ref={reportRef} className="bg-white shadow-2xl rounded-3xl p-10 lg:p-14 print:shadow-none print:rounded-none">
          {/* Header */}
          <div className="flex items-center justify-between border-b-2 border-slate-900 pb-4 mb-6">
            <div className="flex items-center gap-3">
              <BrimiLogo size={44} />
              <div>
                <div className="font-outfit font-black text-2xl text-slate-900">BrimiAI</div>
                <div className="text-xs uppercase tracking-widest text-blue-600 font-bold">Virtual Science Lab</div>
              </div>
            </div>
            <div className="text-right text-xs font-mono text-slate-500">
              <div>Report ID: {report.report_id}</div>
              <div>{new Date(report.created_at).toLocaleString()}</div>
            </div>
          </div>

          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Class {report.grade} · {report.subject}</div>
              <h1 className="font-outfit font-black text-3xl text-slate-900 mt-1" data-testid="report-title">{report.experiment_title}</h1>
              <div className="text-sm text-slate-500 mt-1">Submitted by <span className="font-semibold text-slate-800">{report.user_name}</span></div>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-bold uppercase tracking-widest">
              <Trophy className="h-3.5 w-3.5" /> +25 XP
            </div>
          </div>

          <Section title="Aim" testid="report-aim">{report.aim}</Section>

          <Section title="Apparatus" testid="report-apparatus">
            <ul className="list-disc list-inside space-y-1">
              {report.apparatus.map((a, i) => <li key={i}>{a}</li>)}
            </ul>
          </Section>

          <Section title="Procedure" testid="report-procedure">
            <ol className="list-decimal list-inside space-y-1">
              {report.procedure.map((p, i) => <li key={i}>{p}</li>)}
            </ol>
          </Section>

          <Section title="Parameters Used" testid="report-parameters">
            <table className="w-full text-sm">
              <tbody>
                {Object.entries(report.parameters).map(([k, v]) => (
                  <tr key={k} className="border-b border-slate-100">
                    <td className="py-1.5 text-slate-600 font-mono">{k}</td>
                    <td className="py-1.5 text-slate-900 font-mono text-right">{String(v)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          {report.observations?.length > 0 && (
            <Section title="Observations" testid="report-observations">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-slate-300">
                    <th className="text-left py-2 font-bold">#</th>
                    {Object.keys(report.observations[0]).filter((k) => k !== "ts").map((k) => (
                      <th key={k} className="text-left py-2 font-bold font-mono">{k}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {report.observations.map((o, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="py-1.5 font-mono">{i + 1}</td>
                      {Object.entries(o).filter(([k]) => k !== "ts").map(([k, v]) => (
                        <td key={k} className="py-1.5 font-mono">{String(v)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}

          <Section title="Calculations" testid="report-calculations">
            <div className="font-mono text-sm bg-slate-50 rounded-lg p-4 border border-slate-200">{report.calculations}</div>
          </Section>

          <Section title="Result" testid="report-result">
            <div className="font-semibold text-slate-900">{report.result}</div>
          </Section>

          <Section title="Conclusion" testid="report-conclusion">
            {report.conclusion}
          </Section>

          <Section title="Viva Questions" testid="report-viva">
            <ol className="list-decimal list-inside space-y-2">
              {report.viva.map((v, i) => (
                <li key={i} className="text-slate-800">
                  <span className="font-semibold">{v.q}</span>
                </li>
              ))}
            </ol>
          </Section>

          <div className="mt-10 pt-6 border-t border-slate-200 text-center text-xs font-mono text-slate-400">
            Auto-generated by BrimiAI · Reviewed by AI tutor Brimi 🦉
          </div>
        </div>
      </div>
    </div>
  );
};

export default LabReport;
